/* Papaja quantity cards enhance one native product form; no cart/order writes. */
if (!customElements.get('pp-quantity-bundles')) {
  customElements.define('pp-quantity-bundles', class PpQuantityBundles extends HTMLElement {
    constructor() {
      super();
      this.selectedQuantity = 1;
      this.syncingQuantity = false;
      this.onChoiceChange = this.onChoiceChange.bind(this);
      this.onVariantInputChange = this.onVariantInputChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onNativeQuantityChange = this.onNativeQuantityChange.bind(this);
      this.onShippingCartChange = this.onShippingCartChange.bind(this);
    }

    connectedCallback() {
      this.variants = this.readVariants();
      this.shippingCart = null;
      this.quantityForm = this.closest('.pp-bundle-quantity');
      this.productForm = this.closest('form');
      this.quantityInput = this.quantityForm?.querySelector('input.quantity__input[name="quantity"]');
      this.variantInput = this.productForm?.querySelector('input[name="id"]');
      this.bundleProperty = this.querySelector('[data-pp-bundle-property]');
      this.querySelectorAll('.pp-bundles__radio').forEach(input => input.addEventListener('change', this.onChoiceChange));
      this.variantInput?.addEventListener('change', this.onVariantInputChange);
      this.quantityInput?.addEventListener('change', this.onNativeQuantityChange);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        if (PUB_SUB_EVENTS.cartUpdate) this.unsubscribeShippingCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onShippingCartChange);
      }
      const startingQuantity = Number(this.quantityInput?.value);
      if (Number.isInteger(startingQuantity) && startingQuantity >= 1) this.selectedQuantity = startingQuantity;
      this.refresh(this.currentVariant());
      window.addEventListener('focus', this.onShippingCartChange);
      this.onShippingCartChange();
    }

    disconnectedCallback() {
      this.querySelectorAll('.pp-bundles__radio').forEach(input => input.removeEventListener('change', this.onChoiceChange));
      this.variantInput?.removeEventListener('change', this.onVariantInputChange);
      this.quantityInput?.removeEventListener('change', this.onNativeQuantityChange);
      this.unsubscribeVariantChange?.();
      this.unsubscribeShippingCart?.();
      window.removeEventListener('focus', this.onShippingCartChange);
      this.shippingRequest = (this.shippingRequest || 0) + 1;
      this.shippingAbort?.abort();
      this.quantityForm?.classList.remove('pp-bundle-quantity--enhanced');
    }

    readVariants() {
      try {
        const variants = JSON.parse(this.querySelector('[data-pp-bundle-variants]')?.textContent || 'null');
        return Array.isArray(variants) ? variants : [];
      } catch (_) {
        return [];
      }
    }

    hasValidControls() {
      if (!this.productForm || this.productForm.getAttribute('id') !== this.dataset.formId ||
          this.productForm.dataset.type !== 'add-to-cart-form' ||
          !this.quantityInput || this.quantityInput.form !== this.productForm ||
          !this.variantInput || this.variantInput.form !== this.productForm) return false;
      // One authoritative quantity field: EasySell and native FormData see the same value.
      return Array.from(this.productForm.elements).filter(input => input.name === 'quantity').length === 1;
    }

    currentVariant() {
      const id = this.variantInput?.value;
      if (!id) return undefined;
      return this.variants.find(variant => String(variant.id) === String(id));
    }

    async onShippingCartChange() {
      // Read only: never change the cart to test a card. Revalidate, do not
      // permanently hide badges merely because a normal cart changed.
      const request = this.shippingRequest = (this.shippingRequest || 0) + 1;
      this.shippingAbort?.abort();
      const controller = this.shippingAbort = new AbortController();
      this.shippingCartPending = true;
      this.refreshShippingBadges();
      const timeout = setTimeout(() => controller.abort(), 6000);
      try {
        const url = this.dataset.cartReadUrl;
        if (!url || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\.js$/i.test(url)) throw new Error('Invalid cart read URL');
        const response = await fetch(url, { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Cart unavailable');
        const cart = await response.json();
        if (request !== this.shippingRequest) return;
        this.shippingCart = cart;
      } catch (_) {
        if (request !== this.shippingRequest) return;
        this.shippingCart = null;
      } finally {
        clearTimeout(timeout);
        if (request === this.shippingRequest) {
          this.shippingCartPending = false;
          this.refreshShippingBadges();
        }
      }
    }

    projectedShippingSubtotal(variant, quantity) {
      const cart = this.shippingCart;
      if (!cart || cart.currency !== 'RSD' || !Array.isArray(cart.items)) return null;
      // A recorded coupon can become applicable at a higher quantity even
      // when it is not applicable now. Its conditions require the COD form.
      if ((cart.discount_codes || []).length) return null;
      const isKnownTier = application => {
        const match = /^Papaja \| (2|3|4)\+ komada \| (5|10|15)% popusta$/.exec(application?.title || '');
        // The live AJAX cart also serializes these REST automatic price rules
        // with type=""; accept that shape only with its full scope metadata.
        const automatic = application?.type === 'automatic' || (application?.type === '' &&
          application.target_selection === 'all' && application.target_type === 'line_item' &&
          application.allocation_method === 'across');
        return automatic && application.value_type === 'percentage' &&
          match && Number(match[2]) === (Number(match[1]) - 1) * 5 && Number(application.value) === Number(match[2]);
      };
      if ((cart.cart_level_discount_applications || []).some(application => !isKnownTier(application))) return null;
      let replaced = false;
      const lines = [];
      for (const item of cart.items) {
        if (String(item.product_id) === '7936559874101') return null;
        if ((item.discounts || []).some(discount => Number(discount.amount) > 0 &&
            !/^Papaja \| (2\+ komada \| 5|3\+ komada \| 10|4\+ komada \| 15)% popusta$/.test(discount.title || ''))) return null;
        if ((item.line_level_discount_allocations || item.discount_allocations || []).some(allocation => !isKnownTier(allocation.discount_application))) return null;
        const matching = !replaced && String(item.variant_id) === String(variant.id);
        const units = matching ? quantity : Number(item.quantity);
        const price = matching ? Number(variant.price) : Number(item.original_price);
        if (!Number.isSafeInteger(units) || units < 1 || !Number.isSafeInteger(price) || price < 0) return null;
        if (matching) replaced = true;
        lines.push({ units, price });
      }
      // EasySell sets the first matching variant's quantity via cart/update;
      // it does not add N to its old quantity. Other lines remain untouched.
      if (!replaced) lines.push({ units: quantity, price: Number(variant.price) });
      const units = lines.reduce((sum, line) => sum + line.units, 0);
      const discount = units >= 4 ? 15 : units >= 3 ? 10 : units >= 2 ? 5 : 0;
      // Lower bound protects the exact threshold from allocation rounding.
      return lines.reduce((sum, line) => sum + Math.floor(line.price * line.units * (100 - discount) / 100), 0);
    }

    hasFreeShipping(finalPrice, variant, quantity) {
      const threshold = Number(this.dataset.freeShippingThreshold);
      if (!variant || !Number.isInteger(quantity) || quantity < 1) return false;
      const projectedSubtotal = this.projectedShippingSubtotal(variant, quantity);
      return this.dataset.shippingBadgeEligible === 'true' && !this.shippingCartPending &&
        this.dataset.currency === 'RSD' && Number.isFinite(threshold) && threshold > 0 &&
        Number.isFinite(finalPrice) && finalPrice > threshold && variant.available &&
        Number.isFinite(projectedSubtotal) && projectedSubtotal > threshold;
    }

    refreshShippingBadges() {
      const variant = this.currentVariant();
      this.querySelectorAll('[data-pp-bundle-option]').forEach(option => {
        const quantity = Number(option.dataset.quantity);
        const price = this.optionFinalPrice(option, Number(variant?.price) * quantity);
        const badge = option.querySelector('[data-pp-bundle-shipping]');
        if (badge) badge.hidden = !this.hasFreeShipping(price, variant, quantity);
      });
    }

    optionFinalPrice(option, basePrice) {
      // Fixed amount off (Magična pena exception, owner 02.10.2026) wins over the percent tier.
      const amountOff = option.dataset.amountOff;
      if (amountOff !== undefined && amountOff !== '' && Number.isFinite(Number(amountOff))) {
        return Math.max(0, basePrice - Number(amountOff));
      }
      return Math.round(basePrice * (100 - Number(option.dataset.discount)) / 100);
    }

    setEnhanced(enabled) {
      this.hidden = !enabled;
      this.quantityForm?.classList.toggle('pp-bundle-quantity--enhanced', enabled);
      if (this.bundleProperty) this.bundleProperty.disabled = !enabled;
      if (!enabled) this.querySelectorAll('.pp-bundles__radio').forEach(input => { input.disabled = true; });
    }

    onChoiceChange(event) {
      if (!event.target.matches('.pp-bundles__radio') || !event.target.checked || this.hidden) return;
      this.selectQuantity(Number(event.target.value), true);
      this.onShippingCartChange();
    }

    onNativeQuantityChange() {
      if (this.syncingQuantity) return;
      const value = Number(this.quantityInput.value);
      const radio = Array.from(this.querySelectorAll('.pp-bundles__radio')).find(input => Number(input.value) === value && !input.disabled);
      if (!radio) {
        this.setEnhanced(false);
        return;
      }
      this.selectedQuantity = value;
      radio.checked = true;
      this.updateProperty(value);
    }

    onVariantInputChange() {
      this.refresh(this.currentVariant());
    }

    onVariantChange(event) {
      const data = event?.data;
      if (!data || String(data.sectionId) !== this.dataset.sectionId) return;
      const variant = data.variant;
      if (!variant || String(variant.id) !== this.variantInput?.value) {
        this.setEnhanced(false);
        return;
      }
      // Dawn has now updated quantity rules and reset quantity to its minimum.
      // Preserve this widget's selection, then emit native input/change for EasySell.
      this.refresh(variant);
    }

    getBounds(variant) {
      const rule = variant.quantity_rule;
      const minimum = Number(rule?.min ?? this.quantityInput.dataset.min ?? this.quantityInput.min ?? 1);
      const step = Number(rule?.increment ?? this.quantityInput.step ?? 1);
      const maximumValue = rule ? rule.max : (this.quantityInput.dataset.max ?? this.quantityInput.getAttribute('max'));
      const maximum = maximumValue === null || maximumValue === undefined || maximumValue === '' ? null : Number(maximumValue);
      const cartQuantity = Number(this.quantityInput.dataset.cartQuantity || 0);
      if (!Number.isInteger(minimum) || minimum < 1 || !Number.isInteger(step) || step < 1 ||
          !Number.isInteger(cartQuantity) || cartQuantity < 0 ||
          (maximum !== null && (!Number.isInteger(maximum) || maximum < 0))) return null;
      const remainingMaximum = maximum === null ? null : Math.max(0, maximum - cartQuantity);
      let effectiveMinimum = cartQuantity >= minimum ? Math.min(minimum, step) : minimum;
      if (remainingMaximum !== null) effectiveMinimum = Math.min(effectiveMinimum, remainingMaximum);
      return { minimum: effectiveMinimum, maximum: remainingMaximum, step };
    }

    refresh(variant) {
      if (!this.hasValidControls() || !variant || String(variant.id) !== this.variantInput.value ||
          !Number.isFinite(Number(variant.price)) || Number(variant.price) < 0) {
        this.setEnhanced(false);
        return;
      }
      const bounds = this.getBounds(variant);
      if (!bounds) { this.setEnhanced(false); return; }
      const unitPrice = Number(variant.price);
      const compareAtPrice = Number(variant.compare_at_price) || 0;
      this.querySelectorAll('[data-pp-bundle-option]').forEach(option => {
        const quantity = Number(option.dataset.quantity);
        const basePrice = unitPrice * quantity;
        const finalPrice = this.optionFinalPrice(option, basePrice);
        const reference = quantity === 1 ? compareAtPrice : basePrice;
        const saving = quantity === 1 ? 0 : basePrice - finalPrice;
        const price = option.querySelector('[data-pp-bundle-price]');
        const compare = option.querySelector('[data-pp-bundle-compare]');
        const savingNode = option.querySelector('[data-pp-bundle-saving]');
        const shippingNode = option.querySelector('[data-pp-bundle-shipping]');
        if (shippingNode) shippingNode.hidden = !this.hasFreeShipping(finalPrice, variant, quantity);
        if (price) price.textContent = this.formatMoney(finalPrice);
        if (compare) {
          compare.textContent = reference > finalPrice ? this.formatMoney(reference) : '';
          compare.classList.toggle('pp-bundles__compare--hidden', reference <= finalPrice);
        }
        if (savingNode) {
          savingNode.textContent = saving > 0 ? `Uštedi dodatnih ${this.formatMoney(saving)}` : '';
          savingNode.classList.toggle('pp-bundles__saving--empty', saving <= 0);
        }
        const input = option.querySelector('.pp-bundles__radio');
        input.disabled = !variant.available || quantity < bounds.minimum ||
          (bounds.maximum !== null && quantity > bounds.maximum) || (quantity - bounds.minimum) % bounds.step !== 0;
      });
      const firstAvailable = this.querySelector('.pp-bundles__radio:not(:disabled)');
      if (!firstAvailable) { this.setEnhanced(false); return; }
      this.quantityInput.min = String(bounds.minimum);
      this.quantityInput.step = String(bounds.step);
      if (bounds.maximum === null) this.quantityInput.removeAttribute('max');
      else this.quantityInput.max = String(bounds.maximum);
      this.setEnhanced(true);
      this.selectQuantity(this.selectedQuantity, false);
    }

    updateProperty(quantity) {
      if (this.bundleProperty) this.bundleProperty.value = `${quantity} ${quantity === 1 ? 'komad' : 'komada'}`;
    }

    selectQuantity(quantity, emitChange) {
      if (!this.hasValidControls() || this.hidden) return;
      const radios = Array.from(this.querySelectorAll('.pp-bundles__radio'));
      const selected = radios.find(input => Number(input.value) === quantity && !input.disabled) || radios.find(input => !input.disabled);
      if (!selected) { this.setEnhanced(false); return; }
      quantity = Number(selected.value);
      this.selectedQuantity = quantity;
      selected.checked = true;
      this.updateProperty(quantity);
      const previousValue = this.quantityInput.value;
      this.quantityInput.value = String(quantity);
      if (emitChange || previousValue !== this.quantityInput.value) {
        this.syncingQuantity = true;
        try {
          this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        } finally { this.syncingQuantity = false; }
      }
    }

    formatMoney(cents) {
      const decimals = cents % 100 === 0 ? 0 : 2;
      try {
        return new Intl.NumberFormat('sr-Latn-RS', {
          style: 'currency', currency: this.dataset.currency || 'RSD', currencyDisplay: 'code',
          minimumFractionDigits: decimals, maximumFractionDigits: decimals,
        }).format(cents / 100).replace(/\u00a0/g, ' ');
      } catch (_) {
        return `${(cents / 100).toFixed(decimals).replace('.', ',')} ${this.dataset.currency || 'RSD'}`;
      }
    }
  });
}
